"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useRef } from "react";

const ROUTE_EXIT_DURATION_MS = 170;
const ROUTE_ENTER_DURATION_MS = 280;
const ROUTE_TRANSITION_FALLBACK_MS = 1200;

type PendingDestination = {
  href: string;
  hash: string;
  id: number;
};

type TimeoutRef = {
  current: number | null;
};

function clearTimeoutRef(timeoutRef: TimeoutRef) {
  if (timeoutRef.current === null) {
    return;
  }

  window.clearTimeout(timeoutRef.current);
  timeoutRef.current = null;
}

export function RouteTransitionManager() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const navigationKey = `${pathname}?${searchParams.toString()}`;
  const hasMountedRef = useRef(false);
  const transitionIdRef = useRef(0);
  const pendingDestinationRef = useRef<PendingDestination | null>(null);
  const pushTimeoutRef = useRef<number | null>(null);
  const fallbackTimeoutRef = useRef<number | null>(null);
  const idleTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const root = document.documentElement;

    const resetTransition = () => {
      clearTimeoutRef(pushTimeoutRef);
      clearTimeoutRef(fallbackTimeoutRef);
      clearTimeoutRef(idleTimeoutRef);
      pendingDestinationRef.current = null;
      window.requestAnimationFrame(() => {
        root.dataset.routeTransition = "idle";
      });
    };

    const handleClick = (event: MouseEvent) => {
      if (
        event.defaultPrevented
        || event.button !== 0
        || event.metaKey
        || event.altKey
        || event.ctrlKey
        || event.shiftKey
      ) {
        return;
      }

      const target = event.target;

      if (!(target instanceof Element)) {
        return;
      }

      const link = target.closest<HTMLAnchorElement>("a[href]");

      if (
        !link
        || link.hasAttribute("download")
        || (link.target && link.target !== "_self")
      ) {
        return;
      }

      const currentUrl = new URL(window.location.href);
      const nextUrl = new URL(link.href);

      if (nextUrl.origin !== currentUrl.origin) {
        return;
      }

      const isSamePageAnchor =
        nextUrl.pathname === currentUrl.pathname
        && nextUrl.search === currentUrl.search
        && nextUrl.hash
        && nextUrl.hash !== currentUrl.hash;

      const isSameUrl =
        nextUrl.pathname === currentUrl.pathname
        && nextUrl.search === currentUrl.search
        && nextUrl.hash === currentUrl.hash;

      if (isSamePageAnchor || isSameUrl) {
        return;
      }

      event.preventDefault();

      clearTimeoutRef(pushTimeoutRef);
      clearTimeoutRef(fallbackTimeoutRef);
      clearTimeoutRef(idleTimeoutRef);

      const href = `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
      const destination = {
        href,
        hash: nextUrl.hash,
        id: transitionIdRef.current + 1,
      };

      transitionIdRef.current = destination.id;
      pendingDestinationRef.current = destination;
      root.dataset.routeTransition = "exiting";

      if (!destination.hash) {
        const previousScrollBehavior = root.style.scrollBehavior;
        root.style.scrollBehavior = "auto";
        window.scrollTo({ top: 0, left: 0 });
        root.style.scrollBehavior = previousScrollBehavior;
      }

      pushTimeoutRef.current = window.setTimeout(() => {
        pushTimeoutRef.current = null;
        router.push(href, { scroll: false });
      }, ROUTE_EXIT_DURATION_MS);

      fallbackTimeoutRef.current = window.setTimeout(() => {
        if (pendingDestinationRef.current?.id === destination.id) {
          resetTransition();
        }
      }, ROUTE_TRANSITION_FALLBACK_MS);
    };

    document.addEventListener("click", handleClick, { capture: true });

    return () => {
      document.removeEventListener("click", handleClick, { capture: true });
      clearTimeoutRef(pushTimeoutRef);
      clearTimeoutRef(fallbackTimeoutRef);
      clearTimeoutRef(idleTimeoutRef);
    };
  }, [router]);

  useEffect(() => {
    const root = document.documentElement;

    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      root.dataset.routeTransition = "idle";
      return;
    }

    const destination = pendingDestinationRef.current;

    pendingDestinationRef.current = null;

    if (!destination) {
      root.dataset.routeTransition = "entering";

      const timeoutId = window.setTimeout(() => {
        root.dataset.routeTransition = "idle";
      }, ROUTE_ENTER_DURATION_MS);

      return () => {
        window.clearTimeout(timeoutId);
      };
    }

    const previousScrollBehavior = root.style.scrollBehavior;

    root.style.scrollBehavior = "auto";

    if (destination.hash) {
      const target = document.getElementById(decodeURIComponent(destination.hash.slice(1)));

      if (target) {
        target.scrollIntoView({ block: "start" });
      } else {
        window.scrollTo({ top: 0, left: 0 });
      }
    } else {
      window.scrollTo({ top: 0, left: 0 });
    }

    root.style.scrollBehavior = previousScrollBehavior;
    clearTimeoutRef(fallbackTimeoutRef);
    clearTimeoutRef(idleTimeoutRef);

    window.requestAnimationFrame(() => {
      root.dataset.routeTransition = "entering";

      idleTimeoutRef.current = window.setTimeout(() => {
        root.dataset.routeTransition = "idle";
        idleTimeoutRef.current = null;
      }, ROUTE_ENTER_DURATION_MS);
    });
  }, [navigationKey]);

  return null;
}
